import { List, ListItem } from '$uikit';
import { classNames } from '$utils';
import { v4 as uuidv4 } from 'uuid';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import {
  ActionBottomSheetOption,
  ActionBottomSheetProps,
} from './ActionBottomSheet.interface';
import './ActionBottomSheet.sass';
import React, { MouseEvent, useContext, useEffect, useRef, useState } from 'react';
import { ActionSheetContext } from '$uikit/ActionSheet/ActionSheet';
import { Portal } from '$uikit/Portal/Portal';

const getCurrentOptions = (
  options: ActionBottomSheetOption[],
  path: ActionBottomSheetOption['value'][],
) => {
  const currentPath = [...path];
  const value = currentPath.shift();

  if (path.length) {
    const currentOptions = options.find((option) => option.value == value)?.options;
    if (currentPath.length && currentOptions) {
      return getCurrentOptions(currentOptions, currentPath);
    }
    return currentOptions;
  }
  return options;
};

const getGroupedOptions = (
  options: ActionBottomSheetOption[],
  isAllGrouped?: boolean,
): ActionBottomSheetOption[][] => {
  if (isAllGrouped) {
    return [options];
  }

  const groupedOptions = options.reduce((options, option) => {
    const groupId = option.groupId !== undefined ? option.groupId : uuidv4();

    if (options[groupId]) {
      options[groupId].push(option);
    } else {
      options[groupId] = [option];
    }

    return options;
  }, {});

  return Object.values(groupedOptions);
};

export const ActionBottomSheet = ({
  options,
  title,
  description,
  footer,
  onChange,
  onClose,
  isOpened,
  groups,
  isAllGrouped,
}: ActionBottomSheetProps) => {
  const node = useRef(document.getElementById('action_sheet'));
  const [path, setPath] = useState<ActionBottomSheetOption['value'][]>([]);
  const { active, setActive } = useContext(ActionSheetContext);

  useEffect(() => {
    setActive(isOpened);

    return () => {
      setActive(false);
    };
  }, [isOpened]);

  const handleOption = (e: MouseEvent, option: ActionBottomSheetOption) => {
    e.stopPropagation();

    if (option.disabled) {
      return;
    }
    if (option.options) {
      setPath([...path, option.value]);
      return null;
    }
    onChange(option.value);
    setPath([]);
  };

  if (!isOpened || !node.current) {
    return null;
  }

  return (
    <Portal>
      <BottomSheet
        isActiveControl={active}
        onClose={() => {
          onClose();
          setPath([]);
        }}
        title={title}
        className="ActionBottomSheet"
      >
        <div className="ActionBottomSheet__content">
          {description && (
            <div className="ActionBottomSheet__description">{description}</div>
          )}
          {getGroupedOptions(getCurrentOptions(options, path), isAllGrouped).map(
            (block, blockInd) => {
              return (
                <List
                  title={groups && groups.find((g) => g.id === block[0].groupId)?.label}
                  key={blockInd}
                  className="ActionBottomSheet__block"
                >
                  {block.map((option, optionInd) => {
                    const { label, mode, icon, children } = option;
                    return (
                      <ListItem
                        key={optionInd}
                        icon={icon}
                        onClick={(e) => handleOption(e, option)}
                        className={classNames('ActionBottomSheet__option', {
                          [mode || '']: true,
                        })}
                      >
                        {label}
                        {children}
                      </ListItem>
                    );
                  })}
                </List>
              );
            },
          )}
          {footer && <div className="ActionBottomSheet__footer">{footer}</div>}
        </div>
      </BottomSheet>
    </Portal>
  );
};
